import { ArrowLeft, Copy, Loader2, XCircle } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { JobPostingForm } from '@/components/job-postings/JobPostingForm';
import { toast } from '@/lib/toast';
import { cloneJobPosting, closeJobPosting, getJobPosting, updateJobPosting } from '@/services/jobPostingService';

import type { JobPosting, UpdateJobPostingPayload } from '@/types/jobPosting.types';

export function JobPostingEditPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [posting, setPosting] = useState<JobPosting | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCloseConfirmOpen, setIsCloseConfirmOpen] = useState(false);

  useEffect(() => {
    if (!id) return;

    setIsLoading(true);
    setError(null);

    getJobPosting(id)
      .then((data) => {
        setPosting(data);
        setIsLoading(false);
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : t('jobPostings.errorLoading'));
        setIsLoading(false);
      });
  }, [id, t]);

  const handleUpdate = async (data: UpdateJobPostingPayload) => {
    if (!id) return;
    setIsSubmitting(true);
    try {
      const updated = await updateJobPosting(id, data);
      setPosting(updated);
      toast.success(t('jobPostings.toasts.updateSuccess'));
    } catch {
      toast.error(t('jobPostings.toasts.error'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClone = async () => {
    if (!id) return;
    try {
      const cloned = await cloneJobPosting(id);
      toast.success(t('jobPostings.toasts.cloneSuccess'));
      void navigate(`/job-postings/${cloned.jobPostingId}`);
    } catch {
      toast.error(t('jobPostings.toasts.error'));
    }
  };

  const handleClose = async () => {
    if (!id) return;
    try {
      const closed = await closeJobPosting(id);
      setPosting(closed);
      setIsCloseConfirmOpen(false);
      toast.success(t('jobPostings.toasts.closeSuccess'));
    } catch {
      toast.error(t('jobPostings.toasts.error'));
    }
  };

  const isReadOnly = posting?.status === 'filled' || posting?.status === 'closed';

  if (isLoading) {
    return (
      <div className='flex items-center justify-center min-h-[400px]'>
        <Loader2 className='h-8 w-8 animate-spin text-primary' />
      </div>
    );
  }

  if (error || !posting) {
    return (
      <div className='space-y-6'>
        <div className='flex items-center gap-4'>
          <Link to='/job-postings' className='text-gray-500 hover:text-gray-700'>
            <ArrowLeft size={20} />
          </Link>
          <h1 className='text-2xl font-bold text-gray-900'>{t('jobPostings.editTitle')}</h1>
        </div>
        <div className='bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700'>
          {error ?? t('jobPostings.errorLoading')}
        </div>
      </div>
    );
  }

  return (
    <div className='space-y-6'>
      <div className='flex items-center justify-between'>
        <div className='flex items-center gap-4'>
          <Link to='/job-postings' className='text-gray-500 hover:text-gray-700'>
            <ArrowLeft size={20} />
          </Link>
          <div>
            <h1 className='text-2xl font-bold text-gray-900'>{t('jobPostings.editTitle')}</h1>
            <p className='text-sm text-gray-500'>{posting.postingNumber}</p>
          </div>
        </div>

        <div className='flex items-center gap-2'>
          <button
            type='button'
            onClick={() => void handleClone()}
            className='inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition'>
            <Copy size={14} />
            {t('jobPostings.actions.clone')}
          </button>
          {posting.status !== 'closed' && posting.status !== 'filled' && (
            <button
              type='button'
              onClick={() => setIsCloseConfirmOpen(true)}
              className='inline-flex items-center gap-1.5 rounded-md border border-red-300 bg-white px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50 transition'>
              <XCircle size={14} />
              {t('jobPostings.actions.close')}
            </button>
          )}
        </div>
      </div>

      <div className='bg-white shadow rounded-lg p-6'>
        <JobPostingForm
          initialValues={posting}
          onSubmit={(data) => void handleUpdate(data as UpdateJobPostingPayload)}
          isSubmitting={isSubmitting}
          isReadOnly={isReadOnly}
        />
      </div>

      {/* Close Confirmation Modal */}
      {isCloseConfirmOpen && (
        <div className='fixed inset-0 z-50 flex items-center justify-center bg-black/50'>
          <div className='bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6'>
            <h3 className='text-lg font-semibold text-gray-900 mb-2'>{t('jobPostings.closeConfirm.title')}</h3>
            <p className='text-sm text-gray-600 mb-6'>{t('jobPostings.closeConfirm.message')}</p>
            <div className='flex justify-end gap-3'>
              <button
                type='button'
                onClick={() => setIsCloseConfirmOpen(false)}
                className='rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50'>
                {t('common.no')}
              </button>
              <button
                type='button'
                onClick={() => void handleClose()}
                className='rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700'>
                {t('jobPostings.closeConfirm.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
